import { useEffect, useState } from 'react';
import { updateAlertingConfig, useAlertingConfig } from '../lib/data';
import { parseRecipients, validateRecipients } from '../lib/recipients';

/**
 * Alert-routing settings (R11/R12): SMS recipients and the Slack reminder
 * cadence, editable without a redeploy. Writes go through the rules-validated
 * `admin/alerting` path (E.164 format enforced server-side too).
 */
export function AlertingSettings() {
  const cfg = useAlertingConfig();
  const [recipients, setRecipients] = useState('');
  const [reminder, setReminder] = useState(240);
  const [mentionGroup, setMentionGroup] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (cfg && !touched) {
      setRecipients(cfg.smsRecipients.join(', '));
      setReminder(cfg.reminderIntervalMinutes);
      setMentionGroup(cfg.slackMentionGroupId ?? '');
    }
  }, [cfg, touched]);

  const parsed = parseRecipients(recipients);
  const trimmedGroup = mentionGroup.trim();
  const groupError =
    trimmedGroup !== '' && !/^S[A-Z0-9]{1,20}$/.test(trimmedGroup)
      ? 'Slack group ID must look like S0BGM0CHCRY (starts with S, letters/digits).'
      : null;
  const error =
    validateRecipients(parsed) ??
    (reminder < 15 || reminder > 10080 ? 'Reminder interval must be 15–10080 minutes.' : null) ??
    groupError;

  const save = async () => {
    if (error) return;
    setStatus('saving…');
    try {
      await updateAlertingConfig({
        smsRecipients: parsed,
        reminderIntervalMinutes: reminder,
        slackMentionGroupId: trimmedGroup,
      });
      setStatus('saved ✓');
      setTouched(false);
    } catch {
      setStatus('save failed — check permissions');
    }
  };

  if (!cfg) return <div className="loading">Loading alerting config…</div>;

  return (
    <form className="config-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
      <h2>Alerting</h2>
      <label>
        SMS recipients (comma-separated, E.164 e.g. +14155550123; agent-down alerts only)
        <input value={recipients} onChange={(e) => { setRecipients(e.target.value); setTouched(true); }} />
      </label>
      <label>
        Slack reminder cadence while failing (minutes)
        <input type="number" min={15} max={10080} value={reminder} onChange={(e) => { setReminder(Number(e.target.value)); setTouched(true); }} />
      </label>
      <label>
        Slack user-group ID (S0…) to @-mention on failure alerts — leave empty to disable
        <input value={mentionGroup} onChange={(e) => { setMentionGroup(e.target.value); setTouched(true); }} />
      </label>
      {error && <p className="form-error">{error}</p>}
      <div className="row">
        <button type="submit" disabled={!!error}>Save alerting</button>
        {status && <span className="save-status">{status}</span>}
      </div>
      <p className="save-status">
        SMS requires Quo A2P registration (see issue #1); Slack + the dead-man switch are always active.
      </p>
    </form>
  );
}
